/**
 * Contract Enforcement Service
 * Validates and auto-fixes data before sending to backend
 * Monitors contract violations for analysis
 */

// Contract violation types
export interface ContractViolation {
  field: string;
  expected: string;
  received: string;
  fixApplied: string;
  timestamp: Date;
  context: string;
}

// Contract violation logger
class ContractMonitor {
  private static violations: ContractViolation[] = [];

  static logViolation(violation: ContractViolation): void {
    this.violations.push(violation);

    console.warn('🚨 CONTRACT VIOLATION DETECTED:', {
      field: violation.field,
      expected: violation.expected,
      received: violation.received,
      fixApplied: violation.fixApplied,
      context: violation.context,
    });

    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to monitoring service
      // monitoringService.trackContractViolation(violation);
    }
  }

  static getViolations(): ContractViolation[] {
    return [...this.violations];
  }

  static clearViolations(): void {
    this.violations = [];
  }
}

// Auto-fixer for common contract violations
export class ContractAutoFixer {
  /**
   * Fix Songs metadata to match backend contract
   */
  static fixSongMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    let violationsFixed = 0;

    // 1. Language field must be array
    if (fixed.language && typeof fixed.language === 'string') {
      ContractMonitor.logViolation({
        field: 'language',
        expected: 'string[]',
        received: `string: "${fixed.language}"`,
        fixApplied: `Converted to ["${fixed.language}"]`,
        timestamp: new Date(),
        context,
      });
      fixed.language = [fixed.language];
      violationsFixed++;
    }

    // 2. Ensure arrays for multi-value fields
    const arrayFields = [
      'genre',
      'mood',
      'themes',
      'ageAppropriateness',
      'comprehensiveTags',
    ];
    arrayFields.forEach(field => {
      if (fixed[field] && !Array.isArray(fixed[field])) {
        ContractMonitor.logViolation({
          field,
          expected: 'array',
          received: `${typeof fixed[field]}: ${JSON.stringify(fixed[field])}`,
          fixApplied: `Converted to array`,
          timestamp: new Date(),
          context,
        });
        fixed[field] = [fixed[field]];
        violationsFixed++;
      }
    });

    // 3. Ensure numeric fields are numbers
    const numericFields = ['bpm', 'duration', 'popularity'];
    numericFields.forEach(field => {
      if (
        fixed[field] &&
        typeof fixed[field] === 'string' &&
        !isNaN(Number(fixed[field]))
      ) {
        const originalValue = fixed[field];
        fixed[field] = Number(fixed[field]);
        ContractMonitor.logViolation({
          field,
          expected: 'number',
          received: `string: "${originalValue}"`,
          fixApplied: `Converted to ${fixed[field]}`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    });

    // 4. Ensure string fields for High/Medium/Low values
    const stringFields = [
      'danceability',
      'valence',
      'acousticness',
      'instrumentalness',
      'liveness',
      'speechiness',
      'energy',
    ];
    stringFields.forEach(field => {
      if (fixed[field] && typeof fixed[field] === 'number') {
        const originalValue = fixed[field];
        // Convert numbers back to strings (1=High, 2=Medium, 3=Low)
        const stringValue =
          originalValue === 1
            ? 'High'
            : originalValue === 2
            ? 'Medium'
            : originalValue === 3
            ? 'Low'
            : 'Medium';
        fixed[field] = stringValue;
        ContractMonitor.logViolation({
          field,
          expected: 'string (High/Medium/Low)',
          received: `number: ${originalValue}`,
          fixApplied: `Converted to "${stringValue}"`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    });

    // 5. Null safety for all fields
    Object.keys(fixed).forEach(key => {
      if (fixed[key] === null || fixed[key] === undefined) {
        const defaultValue = arrayFields.includes(key)
          ? []
          : numericFields.includes(key)
          ? 0
          : '';
        if (fixed[key] !== defaultValue) {
          ContractMonitor.logViolation({
            field: key,
            expected: 'non-null value',
            received: 'null/undefined',
            fixApplied: `Set to default: ${JSON.stringify(defaultValue)}`,
            timestamp: new Date(),
            context,
          });
          fixed[key] = defaultValue;
          violationsFixed++;
        }
      }
    });

    if (violationsFixed > 0) {
      console.log(
        `🔧 CONTRACT AUTO-FIX: Fixed ${violationsFixed} violations in ${context}`
      );
    }

    return fixed;
  }

  /**
   * Fix Stars metadata to match backend contract
   */
  static fixStarsMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    // Add stars-specific fixes as needed
    return fixed;
  }

  /**
   * Fix Looks metadata to match backend contract
   */
  static fixLooksMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    let violationsFixed = 0;

    // Ensure arrays for multi-value fields specific to Looks
    const arrayFields = [
      'colors',
      'materials',
      'textures',
      'accessories',
      'patterns',
      'seasons',
    ];
    arrayFields.forEach(field => {
      if (fixed[field] && !Array.isArray(fixed[field])) {
        ContractMonitor.logViolation({
          field,
          expected: 'array',
          received: `${typeof fixed[field]}: ${JSON.stringify(fixed[field])}`,
          fixApplied: `Converted to array`,
          timestamp: new Date(),
          context,
        });
        fixed[field] = [fixed[field]];
        violationsFixed++;
      }
    });

    // Ensure numeric fields are numbers
    const numericFields = ['complexity', 'layerCount'];
    numericFields.forEach(field => {
      if (
        fixed[field] &&
        typeof fixed[field] === 'string' &&
        !isNaN(Number(fixed[field]))
      ) {
        const originalValue = fixed[field];
        fixed[field] = Number(fixed[field]);
        ContractMonitor.logViolation({
          field,
          expected: 'number',
          received: `string: "${originalValue}"`,
          fixApplied: `Converted to ${fixed[field]}`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    });

    if (violationsFixed > 0) {
      console.log(
        `🔧 CONTRACT AUTO-FIX: Fixed ${violationsFixed} violations in ${context}`
      );
    }

    return fixed;
  }

  /**
   * Fix Moves metadata to match backend contract
   */
  static fixMovesMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    let violationsFixed = 0;

    // Ensure arrays for multi-value fields specific to Moves
    const arrayFields = [
      'movementTypes',
      'bodyParts',
      'techniques',
      'musicGenres',
    ];
    arrayFields.forEach(field => {
      if (fixed[field] && !Array.isArray(fixed[field])) {
        ContractMonitor.logViolation({
          field,
          expected: 'array',
          received: `${typeof fixed[field]}: ${JSON.stringify(fixed[field])}`,
          fixApplied: `Converted to array`,
          timestamp: new Date(),
          context,
        });
        fixed[field] = [fixed[field]];
        violationsFixed++;
      }
    });

    // Ensure numeric fields are numbers
    const numericFields = ['tempo', 'duration', 'intensity', 'difficulty'];
    numericFields.forEach(field => {
      if (
        fixed[field] &&
        typeof fixed[field] === 'string' &&
        !isNaN(Number(fixed[field]))
      ) {
        const originalValue = fixed[field];
        fixed[field] = Number(fixed[field]);
        ContractMonitor.logViolation({
          field,
          expected: 'number',
          received: `string: "${originalValue}"`,
          fixApplied: `Converted to ${fixed[field]}`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    });

    if (violationsFixed > 0) {
      console.log(
        `🔧 CONTRACT AUTO-FIX: Fixed ${violationsFixed} violations in ${context}`
      );
    }

    return fixed;
  }

  /**
   * Fix Worlds metadata to match backend contract
   */
  static fixWorldsMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    let violationsFixed = 0;

    // Ensure arrays for multi-value fields specific to Worlds
    const arrayFields = [
      'environments',
      'landmarks',
      'atmospheres',
      'activities',
      'themes',
    ];
    arrayFields.forEach(field => {
      if (fixed[field] && !Array.isArray(fixed[field])) {
        ContractMonitor.logViolation({
          field,
          expected: 'array',
          received: `${typeof fixed[field]}: ${JSON.stringify(fixed[field])}`,
          fixApplied: `Converted to array`,
          timestamp: new Date(),
          context,
        });
        fixed[field] = [fixed[field]];
        violationsFixed++;
      }
    });

    // Ensure numeric fields are numbers
    const numericFields = ['scale', 'population', 'timeOfDay'];
    numericFields.forEach(field => {
      if (
        fixed[field] &&
        typeof fixed[field] === 'string' &&
        !isNaN(Number(fixed[field]))
      ) {
        const originalValue = fixed[field];
        fixed[field] = Number(fixed[field]);
        ContractMonitor.logViolation({
          field,
          expected: 'number',
          received: `string: "${originalValue}"`,
          fixApplied: `Converted to ${fixed[field]}`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    });

    if (violationsFixed > 0) {
      console.log(
        `🔧 CONTRACT AUTO-FIX: Fixed ${violationsFixed} violations in ${context}`
      );
    }

    return fixed;
  }

  /**
   * Fix Composite metadata to match backend contract
   */
  static fixCompositeMetadata(data: any, context: string = 'unknown'): any {
    const fixed = { ...data };
    let violationsFixed = 0;

    // Ensure components is an array
    if (fixed.components && !Array.isArray(fixed.components)) {
      ContractMonitor.logViolation({
        field: 'components',
        expected: 'array',
        received: `${typeof fixed.components}`,
        fixApplied: `Converted to array`,
        timestamp: new Date(),
        context,
      });
      fixed.components = [fixed.components];
      violationsFixed++;
    }

    // Filter out invalid components
    if (Array.isArray(fixed.components)) {
      const originalCount = fixed.components.length;
      fixed.components = fixed.components.filter(
        (c: any) => c && typeof c === 'object' && (c.id || c.hfn || c.name)
      );
      if (fixed.components.length !== originalCount) {
        ContractMonitor.logViolation({
          field: 'components',
          expected: 'valid component objects',
          received: `array with ${
            originalCount - fixed.components.length
          } invalid items`,
          fixApplied: `Filtered to ${fixed.components.length} valid components`,
          timestamp: new Date(),
          context,
        });
        violationsFixed++;
      }
    }

    if (violationsFixed > 0) {
      console.log(
        `🔧 CONTRACT AUTO-FIX: Fixed ${violationsFixed} violations in ${context}`
      );
    }

    return fixed;
  }

  /**
   * Fix AI metadata before sending to backend
   */
  static fixAiMetadata(data: any, context: string = 'unknown'): any {
    if (!data) return data;

    const fixed = { ...data };

    // Remove null values
    if (fixed.songContext === null) fixed.songContext = '';
    if (fixed.remixContext === null) fixed.remixContext = '';

    return fixed;
  }
}

// Main contract enforcement service
export class ContractEnforcer {
  /**
   * Validate and auto-fix asset data before backend submission
   */
  static enforceAssetContract(assetData: any): any {
    console.log(
      '🛡️ ENFORCING CONTRACTS: Validating asset data before backend submission'
    );

    const enforced = { ...assetData };

    // COMPREHENSIVE LANGUAGE FIELD FIX: Check all possible locations
    ContractEnforcer.fixAllLanguageFields(enforced, 'asset-creation-root');

    // Fix layer-specific metadata
    if (enforced.songMetadata) {
      enforced.songMetadata = ContractAutoFixer.fixSongMetadata(
        enforced.songMetadata,
        'asset-creation-songMetadata'
      );
      // Double-check language fields in songMetadata after processing
      ContractEnforcer.fixAllLanguageFields(
        enforced.songMetadata,
        'asset-creation-songMetadata-post'
      );
    }

    // CRITICAL FIX: Only process starMetadata for S layer assets
    if ((enforced.starsMetadata || enforced.starMetadata) && enforced.layer === 'S') {
      const starData = enforced.starsMetadata || enforced.starMetadata;
      const fixedStarData = ContractAutoFixer.fixStarsMetadata(
        starData,
        'asset-creation-starsMetadata'
      );

      // CRITICAL FIX: Preserve both field names for backend compatibility
      enforced.starsMetadata = fixedStarData;
      enforced.starMetadata = fixedStarData;

      // Check language fields in star metadata
      ContractEnforcer.fixAllLanguageFields(
        enforced.starsMetadata,
        'asset-creation-starsMetadata-post'
      );
      ContractEnforcer.fixAllLanguageFields(
        enforced.starMetadata,
        'asset-creation-starMetadata-post'
      );
    } else if ((enforced.starsMetadata || enforced.starMetadata) && enforced.layer !== 'S') {
      // Remove starMetadata from non-S layer assets (anti-bloat)
      delete enforced.starsMetadata;
      delete enforced.starMetadata;
      console.log(`🔧 [ANTI-BLOAT] Removed starMetadata from ${enforced.layer} layer asset`);
    }

    // Fix Looks metadata
    if (enforced.looksMetadata || enforced.lookMetadata) {
      const lookData = enforced.looksMetadata || enforced.lookMetadata;
      const fixedLookData = ContractAutoFixer.fixLooksMetadata(
        lookData,
        'asset-creation-looksMetadata'
      );

      // Apply language field normalization
      ContractEnforcer.fixAllLanguageFields(
        fixedLookData,
        'asset-creation-looksMetadata-post'
      );

      // Preserve both field names for backend compatibility
      enforced.looksMetadata = fixedLookData;
      enforced.lookMetadata = fixedLookData;
    }

    // Fix Moves metadata
    if (enforced.movesMetadata || enforced.moveMetadata) {
      const moveData = enforced.movesMetadata || enforced.moveMetadata;
      const fixedMoveData = ContractAutoFixer.fixMovesMetadata(
        moveData,
        'asset-creation-movesMetadata'
      );

      // Apply language field normalization
      ContractEnforcer.fixAllLanguageFields(
        fixedMoveData,
        'asset-creation-movesMetadata-post'
      );

      // Preserve both field names for backend compatibility
      enforced.movesMetadata = fixedMoveData;
      enforced.moveMetadata = fixedMoveData;
    }

    // Fix Worlds metadata
    if (enforced.worldsMetadata || enforced.worldMetadata) {
      const worldData = enforced.worldsMetadata || enforced.worldMetadata;
      const fixedWorldData = ContractAutoFixer.fixWorldsMetadata(
        worldData,
        'asset-creation-worldsMetadata'
      );

      // Apply language field normalization
      ContractEnforcer.fixAllLanguageFields(
        fixedWorldData,
        'asset-creation-worldsMetadata-post'
      );

      // Preserve both field names for backend compatibility
      enforced.worldsMetadata = fixedWorldData;
      enforced.worldMetadata = fixedWorldData;
    }

    // Fix Composite metadata
    if (enforced.compositeMetadata) {
      const compositeData = enforced.compositeMetadata;
      const fixedCompositeData = ContractAutoFixer.fixCompositeMetadata(
        compositeData,
        'asset-creation-compositeMetadata'
      );

      // Apply language field normalization
      ContractEnforcer.fixAllLanguageFields(
        fixedCompositeData,
        'asset-creation-compositeMetadata-post'
      );

      enforced.compositeMetadata = fixedCompositeData;
    }

    // Fix AI metadata
    if (enforced.aiMetadata) {
      enforced.aiMetadata = ContractAutoFixer.fixAiMetadata(
        enforced.aiMetadata,
        'asset-creation-aiMetadata'
      );
      // Check language fields in AI metadata
      ContractEnforcer.fixAllLanguageFields(
        enforced.aiMetadata,
        'asset-creation-aiMetadata-post'
      );

      // Also check nested layer metadata within AI metadata
      if (enforced.aiMetadata.looksMetadata) {
        ContractEnforcer.fixAllLanguageFields(
          enforced.aiMetadata.looksMetadata,
          'asset-creation-aiMetadata-looksMetadata'
        );
      }
      if (enforced.aiMetadata.movesMetadata) {
        ContractEnforcer.fixAllLanguageFields(
          enforced.aiMetadata.movesMetadata,
          'asset-creation-aiMetadata-movesMetadata'
        );
      }
      if (enforced.aiMetadata.worldsMetadata) {
        ContractEnforcer.fixAllLanguageFields(
          enforced.aiMetadata.worldsMetadata,
          'asset-creation-aiMetadata-worldsMetadata'
        );
      }
      if (enforced.aiMetadata.compositeMetadata) {
        ContractEnforcer.fixAllLanguageFields(
          enforced.aiMetadata.compositeMetadata,
          'asset-creation-aiMetadata-compositeMetadata'
        );
      }
    }

    // Log summary
    const violations = ContractMonitor.getViolations();
    const recentViolations = violations.filter(
      v => Date.now() - v.timestamp.getTime() < 1000 // Last second
    );

    if (recentViolations.length > 0) {
      console.log(
        `✅ CONTRACT ENFORCEMENT COMPLETE: Fixed ${recentViolations.length} violations`
      );
    } else {
      console.log('✅ CONTRACT ENFORCEMENT COMPLETE: No violations detected');
    }

    return enforced;
  }

  /**
   * Comprehensive language field normalization - checks ALL possible locations
   * Based on backend normalization paths from language-field-fix.ts
   */
  static fixAllLanguageFields(data: any, context: string): void {
    if (!data || typeof data !== 'object') return;

    // COMPREHENSIVE language field paths based on backend validation
    const languagePaths = [
      'language',
      'languages', // plural variant
      'lang', // short variant
      'songMetadata.language',
      'songMetadata.languages',
      'layerMetadata.language',
      'layerMetadata.languages',
      'starsMetadata.language',
      'starsMetadata.languages',
      'starMetadata.language',
      'starMetadata.languages',
      'aiMetadata.language',
      'aiMetadata.languages',
      'aiMetadata.songMetadata.language',
      'aiMetadata.songMetadata.languages',
      'aiMetadata.layerMetadata.language',
      'aiMetadata.layerMetadata.languages',
      'aiMetadata.starsMetadata.language',
      'aiMetadata.starsMetadata.languages',
      'metadata.language',
      'metadata.languages',
      // Additional layer metadata paths
      'lookMetadata.language',
      'lookMetadata.languages',
      'moveMetadata.language',
      'moveMetadata.languages',
      'worldMetadata.language',
      'worldMetadata.languages',
      'compositeMetadata.language',
      'compositeMetadata.languages',
      // Root level variants that might exist
      'songLanguage',
      'songLanguages',
      'trackLanguage',
      'trackLanguages',
      'audioLanguage',
      'audioLanguages',
    ];

    // AGGRESSIVE SCAN: Also check for any field containing "language" or "lang"
    ContractEnforcer.scanForLanguageFields(data, context);

    languagePaths.forEach(path => {
      const value = ContractEnforcer.getNestedValue(data, path);
      if (value !== undefined && value !== null) {
        if (typeof value === 'string') {
          // Convert string to array
          ContractEnforcer.setNestedValue(data, path, [value]);
          ContractMonitor.logViolation({
            field: `${path}`,
            expected: 'string[]',
            received: `string: "${value}"`,
            fixApplied: `Converted to ["${value}"]`,
            timestamp: new Date(),
            context: `${context}-language-normalization`,
          });
          console.log(
            `🔧 [LANGUAGE FIELD FIX] ${path}: "${value}" → ["${value}"]`
          );
        } else if (Array.isArray(value)) {
          // Ensure array contains valid values
          const filtered = value.filter(v => {
            if (!v) return false;
            const stringValue = typeof v === 'string' ? v : String(v);
            return stringValue && stringValue.trim() !== '';
          });
          if (filtered.length === 0) {
            ContractEnforcer.setNestedValue(data, path, ['English']);
            ContractMonitor.logViolation({
              field: `${path}`,
              expected: 'string[] with valid values',
              received: 'empty array',
              fixApplied: 'Set to ["English"]',
              timestamp: new Date(),
              context: `${context}-language-default`,
            });
          } else if (filtered.length !== value.length) {
            ContractEnforcer.setNestedValue(data, path, filtered);
            ContractMonitor.logViolation({
              field: `${path}`,
              expected: 'string[] with valid values',
              received: 'array with empty values',
              fixApplied: `Filtered to [${filtered.join(', ')}]`,
              timestamp: new Date(),
              context: `${context}-language-filter`,
            });
          }
        } else {
          // Convert any other type to default array
          ContractEnforcer.setNestedValue(data, path, ['English']);
          ContractMonitor.logViolation({
            field: `${path}`,
            expected: 'string[]',
            received: `${typeof value}: ${JSON.stringify(value)}`,
            fixApplied: 'Set to ["English"]',
            timestamp: new Date(),
            context: `${context}-language-type-fix`,
          });
        }
      }
    });
  }

  /**
   * Aggressive scan for any field containing "language" or "lang"
   * This catches field names we might not have anticipated
   */
  static scanForLanguageFields(
    obj: any,
    context: string,
    path: string = ''
  ): void {
    if (!obj || typeof obj !== 'object') return;

    Object.keys(obj).forEach(key => {
      const currentPath = path ? `${path}.${key}` : key;
      const value = obj[key];

      // Check if the key contains language-related terms
      if (
        key.toLowerCase().includes('language') ||
        key.toLowerCase().includes('lang')
      ) {
        if (
          value !== undefined &&
          value !== null &&
          typeof value === 'string'
        ) {
          // Convert string to array
          obj[key] = [value];
          ContractMonitor.logViolation({
            field: `${currentPath} (discovered)`,
            expected: 'string[]',
            received: `string: "${value}"`,
            fixApplied: `Converted to ["${value}"]`,
            timestamp: new Date(),
            context: `${context}-aggressive-scan`,
          });
          console.log(
            `🔧 [AGGRESSIVE LANGUAGE FIX] ${currentPath}: "${value}" → ["${value}"]`
          );
        }
      }

      // Recursively scan nested objects
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        ContractEnforcer.scanForLanguageFields(value, context, currentPath);
      }
    });
  }

  /**
   * Helper to get nested value by path
   */
  static getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  /**
   * Helper to set nested value by path
   */
  static setNestedValue(obj: any, path: string, value: any): void {
    const props = path.split('.');
    const lastProp = props.pop()!;
    const target = props.reduce((current, prop) => {
      if (!current[prop]) current[prop] = {};
      return current[prop];
    }, obj);
    target[lastProp] = value;
  }

  /**
   * Get contract violation statistics
   */
  static getViolationStats(): {
    total: number;
    byField: Record<string, number>;
    byContext: Record<string, number>;
    recent: ContractViolation[];
  } {
    const violations = ContractMonitor.getViolations();

    const byField: Record<string, number> = {};
    const byContext: Record<string, number> = {};

    violations.forEach(v => {
      byField[v.field] = (byField[v.field] || 0) + 1;
      byContext[v.context] = (byContext[v.context] || 0) + 1;
    });

    const recent = violations
      .filter(v => Date.now() - v.timestamp.getTime() < 300000) // Last 5 minutes
      .slice(-10); // Last 10

    return {
      total: violations.length,
      byField,
      byContext,
      recent,
    };
  }
}

export default ContractEnforcer;
